import React, { useContext, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLabels, useRequestData } from 'wsm-common-data';
import { trackEvent } from 'ddc-track-event';
import { FiltersContext, IncentivesContext } from '../contexts';
import { getLiveFilterCount } from '../utils/filterVehicles';
import { mobileShowVehiclesEvent } from '../utils/wueTracking';
import FacetGroupsSection from './FacetGroupsSection';

const MobileFilterFlyout = ({ isOpen, onClose }) => {
	const { activeFilters } = useContext(FiltersContext);
	const { incentives, showcaseMappings } = useContext(IncentivesContext);
	const labels = useLabels();
	const { widgetName, windowId } = useRequestData();
	const asideRef = useRef(null);

	const liveFilterCount = useMemo(
		() => getLiveFilterCount(incentives, activeFilters, showcaseMappings),
		[incentives, activeFilters, showcaseMappings]
	);

	// Body scroll lock
	useEffect(() => {
		if (isOpen) {
			document.body.classList.add('flyout-open');
		} else {
			document.body.classList.remove('flyout-open');
		}
		return () => document.body.classList.remove('flyout-open');
	}, [isOpen]);

	// Focus first focusable element when opened
	useEffect(() => {
		if (isOpen && asideRef.current) {
			const focusable = asideRef.current.querySelector(
				'button, [href], input, select, [tabindex]:not([tabindex="-1"])'
			);
			focusable?.focus();
		}
	}, [isOpen]);

	const handleKeyDown = (e) => {
		if (e.key === 'Escape') {
			onClose();
			return;
		}
		if (e.key === 'Tab') {
			const focusable = asideRef.current?.querySelectorAll(
				'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
			);
			if (!focusable?.length) return;
			const first = focusable[0];
			const last = focusable[focusable.length - 1];
			if (e.shiftKey && document.activeElement === first) {
				e.preventDefault();
				last.focus();
			} else if (!e.shiftKey && document.activeElement === last) {
				e.preventDefault();
				first.focus();
			}
		}
	};

	const handleApplyAndClose = () => {
		trackEvent(
			widgetName,
			windowId,
			mobileShowVehiclesEvent({ liveFilterCount })
		);
		onClose();
		const results = document.querySelector('.incentives-search');
		results?.scrollIntoView({ behavior: 'smooth' });
	};

	return (
		<>
			{/* Backdrop */}
			<div
				className={`mobile-flyout-backdrop${isOpen ? ' show' : ''}`}
				onClick={onClose}
				aria-hidden="true"
			/>

			{/* Flyout drawer */}
			{/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
			<div
				ref={asideRef}
				className={`mobile-filter-flyout d-flex flex-column${
					isOpen ? ' open' : ''
				}`}
				role="dialog"
				aria-modal="true"
				aria-label="Filter incentives"
				tabIndex={-1}
				onKeyDown={handleKeyDown}
			>
				{/* Header */}
				<div className="flyout-header d-flex justify-content-between align-items-center px-3 py-3">
					<h2 className="ddc-font-size-large my-3">
						{labels.get('INCENTIVES_FILTER_SORT')}
					</h2>
					<button
						type="button"
						className="btn btn-link p-0"
						onClick={onClose}
					>
						{labels.get('DONE')}
					</button>
				</div>

				{/* Scrollable body: facets (chips rendered inside FacetGroupsSection) */}
				<div className="flyout-body flex-grow-1 overflow-y-auto px-3">
					<FacetGroupsSection />
				</div>

				{/* Sticky footer CTA */}
				<div className="flyout-footer border-top px-3 py-3">
					<button
						type="button"
						className="btn btn-primary w-100"
						onClick={handleApplyAndClose}
					>
						<span aria-live="polite">{liveFilterCount}</span>{' '}
						{labels.get('INCENTIVES_SHOW_X_INCENTIVES')}
					</button>
				</div>
			</div>
		</>
	);
};

MobileFilterFlyout.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired
};

export default MobileFilterFlyout;
